import { useCallback, useEffect, useRef } from 'react';
import { FEATURE_FLAGS } from '../../../types/featureFlags';
import { hasFeature } from '../../../utils/feature-util';
import {
  CONNECTION_STATE,
  ConnectionState,
  useConnectionRetryStore,
} from '../stores/connectionRetryStore';

/**
 * Connection timeout in milliseconds (12 seconds)
 */
const CONNECTION_TIMEOUT_MS = 12000;

/**
 * Default error message for connection timeout
 */
const TIMEOUT_ERROR_MESSAGE =
  "We couldn't connect to AI Scribe right now.\nPlease try again.";

/**
 * Button configuration based on connection state
 */
interface ButtonConfig {
  label: string;
  enabled: boolean;
  tooltip?: string;
}

/**
 * Return type for useConnectionRetry hook
 */
interface UseConnectionRetryResult {
  connectionState: ConnectionState;
  buttonConfig: ButtonConfig;
  transitionTo: (state: ConnectionState, errorMessage?: string) => void;
}

/**
 * Maps connection state to button configuration
 */
const getButtonConfig = (
  state: ConnectionState,
  errorMessage?: string
): ButtonConfig => {
  switch (state) {
    case CONNECTION_STATE.CONNECTING:
    case CONNECTION_STATE.RECONNECTING:
      return {
        label: 'Connecting…',
        enabled: false,
        tooltip: 'AI Scribe is getting ready.\nPlease wait a moment.',
      };

    case CONNECTION_STATE.READY:
      return {
        label: 'Start Transcribing',
        enabled: true,
        tooltip: undefined,
      };

    case CONNECTION_STATE.ERROR:
      return {
        label: 'Retry Connection',
        enabled: true,
        tooltip: errorMessage,
      };

    case CONNECTION_STATE.DISCONNECTED:
    default:
      return {
        label: 'Start Transcribing',
        enabled: true,
        tooltip: undefined,
      };
  }
};

/**
 * Hook for managing WebSocket connection state machine and timeout handling
 * for the Start Transcribing button.
 *
 * Behavior is controlled by FEATURE_FLAGS.CONNECTION_RETRY:
 * - Flag OFF: Returns null, no behavior changes
 * - Flag ON: Returns connection state, button config, and transition function
 *
 * Features when enabled:
 * - State machine: disconnected → connecting → ready/reconnecting/error
 * - 12-second timeout: Auto-transitions to error if stuck in connecting/reconnecting
 * - Button UX: Dynamic label, enabled state, and tooltip based on connection state
 *
 * @returns Connection retry state and controls, or null if feature flag is disabled
 */
export const useConnectionRetry = (): UseConnectionRetryResult | null => {
  const isFeatureEnabled = hasFeature(FEATURE_FLAGS.CONNECTION_RETRY);

  const connectionState = useConnectionRetryStore(
    (state) => state.connectionState
  );
  const errorMessage = useConnectionRetryStore((state) => state.errorMessage);
  const setConnectionState = useConnectionRetryStore(
    (state) => state.setConnectionState
  );

  const timeoutRef = useRef<number | null>(null);

  /**
   * Clears any active timeout timer
   */
  const clearTimeoutTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * Starts the connection timeout timer
   * Transitions to error state after CONNECTION_TIMEOUT_MS
   */
  const startTimeoutTimer = useCallback(() => {
    clearTimeoutTimer();

    timeoutRef.current = window.setTimeout(() => {
      setConnectionState(CONNECTION_STATE.ERROR, TIMEOUT_ERROR_MESSAGE);
    }, CONNECTION_TIMEOUT_MS);
  }, [clearTimeoutTimer, setConnectionState]);

  /**
   * Transitions to a new connection state
   * Manages timeout timer based on state transitions
   */
  const transitionTo = useCallback(
    (newState: ConnectionState, newErrorMessage?: string) => {
      setConnectionState(newState, newErrorMessage);

      if (
        newState === CONNECTION_STATE.CONNECTING ||
        newState === CONNECTION_STATE.RECONNECTING
      ) {
        startTimeoutTimer();
      } else {
        clearTimeoutTimer();
      }
    },
    [setConnectionState, startTimeoutTimer, clearTimeoutTimer]
  );

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      clearTimeoutTimer();
    };
  }, [clearTimeoutTimer]);

  if (!isFeatureEnabled) {
    return null;
  }

  const buttonConfig = getButtonConfig(connectionState, errorMessage);

  return {
    connectionState,
    buttonConfig,
    transitionTo,
  };
};
